import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard, ChefHat, AlertTriangle, ClipboardCheck, Trash2, Bell,
  ArrowRight, Zap, CheckCircle2, RefreshCw, Store, Package, Factory,
  Sparkles, Info, ShieldAlert,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";

/* Kitchen Operations Dashboard — Phase 3 (composition layer).
   Head Chef's single home screen. ALL data comes from the existing composition
   endpoint /api/kitchen-ops/dashboard which reuses:
     - Phase 1 + 2A prep_queue (readiness / prep summary / critical RTC)
     - Phase 2B sop_today (SOP status per category)
     - Existing waste_reports collection (waste today)
     - Existing notifications engine (alerts today)
   NO business logic here — this file only PRESENTS composed data and NAVIGATES
   to the underlying operational pages where the actual work is performed. */

const BAND_TONE = { ready: "success", partial: "warn", not_ready: "danger" };
const BAND_LABEL = { ready: "Siap", partial: "Sebagian Siap", not_ready: "Belum Siap" };
const BAND_EMOJI = { ready: "🟢", partial: "🟡", not_ready: "🔴" };
const BAND_TEXT_CLS = {
  ready: "text-emerald-600",
  partial: "text-amber-600",
  not_ready: "text-red-600",
};

const SOP_STATUS_TONE = {
  complete: "success", blocked: "danger", attention: "warn", in_progress: "mute",
};
const SOP_STATUS_LABEL = {
  complete: "Selesai", blocked: "Diblokir", attention: "Perlu Perhatian", in_progress: "Dalam Proses",
};

function StatCard({ label, value, sublabel, tone = "mute", icon: Icon, testId }) {
  const toneCls = {
    mute: "text-ink",
    success: "text-emerald-600",
    warn: "text-amber-600",
    danger: "text-red-600",
    info: "text-blue-600",
  }[tone] || "text-ink";
  return (
    <div className="rounded-xl border border-line bg-white p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-mute">
        {Icon && <Icon size={14} />}
        <span>{label}</span>
      </div>
      <p className={`mt-2 text-2xl font-black tabular-nums ${toneCls}`}>{value}</p>
      {sublabel && <p className="mt-0.5 text-xs text-ink-mute">{sublabel}</p>}
    </div>
  );
}

function SectionCard({ title, subtitle, icon: Icon, action, children, testId }) {
  return (
    <Card className="p-4 sm:p-5" data-testid={testId}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          {Icon && (
            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
              <Icon size={16} />
            </div>
          )}
          <div>
            <h3 className="text-base font-bold text-ink">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-ink-mute">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

function ReadinessCard({ readiness, onOpen }) {
  const band = readiness?.band || "not_ready";
  const score = readiness?.score ?? 0;
  return (
    <Card className="p-5" data-testid="kops-readiness">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-mute">
          <Zap size={14} /> Kitchen Readiness
        </div>
        <Badge tone={BAND_TONE[band]}>{BAND_EMOJI[band]} {BAND_LABEL[band]}</Badge>
      </div>
      <div className={`mt-3 flex items-end gap-2 ${BAND_TEXT_CLS[band]}`}>
        <span className="text-5xl font-black tabular-nums" data-testid="kops-readiness-score">{score}%</span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-line">
        <div
          className={`h-full transition-all ${band === "ready" ? "bg-emerald-500" : band === "partial" ? "bg-amber-500" : "bg-red-500"}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
        <div className="rounded bg-emerald-50 p-1.5 text-emerald-700"><b>{readiness?.complete ?? 0}</b><br/>Selesai</div>
        <div className="rounded bg-amber-50 p-1.5 text-amber-700"><b>{readiness?.partial ?? 0}</b><br/>Sebagian</div>
        <div className="rounded bg-slate-100 p-1.5 text-slate-700"><b>{readiness?.not_started ?? 0}</b><br/>Belum</div>
        <div className="rounded bg-red-50 p-1.5 text-red-700"><b>{readiness?.critical ?? 0}</b><br/>Kritis</div>
      </div>
      <Button variant="ghost" size="sm" className="mt-4 w-full" onClick={onOpen} data-testid="kops-readiness-open">
        Buka Meja Prepare <ArrowRight size={14} />
      </Button>
    </Card>
  );
}

function TodayPrepCard({ summary, onOpen }) {
  const total = summary?.total ?? 0;
  const complete = summary?.complete ?? 0;
  const remaining = Math.max(0, total - complete);
  return (
    <SectionCard
      title="Persiapan Hari Ini"
      subtitle={`Total ${total} item · Selesai ${complete} · Sisa ${remaining}`}
      icon={ChefHat}
      testId="kops-today-prep"
      action={<Button size="sm" variant="ghost" onClick={onOpen} data-testid="kops-today-prep-open">Buka Meja Prepare <ArrowRight size={14} /></Button>}
    >
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Total" value={total} icon={Package} testId="kops-prep-total" />
        <StatCard label="Selesai" value={complete} tone="success" icon={CheckCircle2} testId="kops-prep-complete" />
        <StatCard label="Sisa" value={remaining} tone="warn" icon={ChefHat} testId="kops-prep-remaining" />
        <StatCard label="Kritis" value={summary?.critical ?? 0} tone="danger" icon={AlertTriangle} testId="kops-prep-critical" />
      </div>
    </SectionCard>
  );
}

function CriticalRtcCard({ items, onPrepare }) {
  const list = items || [];
  return (
    <SectionCard
      title="Bahan Siap Masak Kritis (di bawah minimum)"
      subtitle={list.length ? `${list.length} item butuh prioritas` : "Semua Bahan Siap Masak di atas minimum"}
      icon={AlertTriangle}
      testId="kops-critical-rtc"
    >
      {!list.length && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          🟢 Tidak ada item Bahan Siap Masak di bawah minimum. Bagus!
        </p>
      )}
      {list.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line" data-testid="kops-critical-rtc-list">
          {list.map((it) => (
            <li key={it.output_product_id} className="flex items-center gap-3 p-3" data-testid={`kops-critical-item-${it.output_product_id}`}>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{it.output_name}</p>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-mute">
                  <span>Saat ini: <b className="text-red-600 tabular-nums">{it.current_stock ?? 0} {it.unit}</b></span>
                  <span>Minimum: <b className="tabular-nums">{it.min_qty ?? "—"} {it.unit}</b></span>
                  <span>Target: <b className="tabular-nums">{it.target_qty} {it.unit}</b></span>
                  <span>Butuh: <b className="text-red-600 tabular-nums">{it.need_prepare} {it.unit}</b></span>
                </div>
              </div>
              <Badge tone="danger">🔴 Kritis</Badge>
              <Button size="sm" onClick={() => onPrepare(it)} data-testid={`kops-critical-prepare-${it.output_product_id}`}>
                Prepare <ArrowRight size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function SopStatusCard({ categories, blockersTotal, warningsTotal, onOpen, openLabel }) {
  const list = categories || [];
  return (
    <SectionCard
      title="Kitchen SOP"
      subtitle={
        blockersTotal
          ? `🔴 ${blockersTotal} pemblokir · 🟡 ${warningsTotal} peringatan`
          : (warningsTotal ? `🟡 ${warningsTotal} peringatan` : "Tidak ada pemblokir SOP")
      }
      icon={ClipboardCheck}
      testId="kops-sop-status"
      action={<Button size="sm" variant="ghost" onClick={onOpen} data-testid="kops-sop-open">{openLabel} <ArrowRight size={14} /></Button>}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {list.map((c) => (
          <div key={c.category} className="rounded-lg border border-line p-3" data-testid={`kops-sop-cat-${c.category}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-ink">{c.label}</p>
              <Badge tone={SOP_STATUS_TONE[c.status]}>{SOP_STATUS_LABEL[c.status]}</Badge>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-ink-mute">
              <span>Selesai <b className="tabular-nums">{c.passed}/{c.total}</b></span>
              <span className="tabular-nums font-semibold">{c.progress_pct}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-line">
              <div
                className={`h-full ${c.status === "complete" ? "bg-emerald-500" : c.status === "blocked" ? "bg-red-500" : c.status === "attention" ? "bg-amber-500" : "bg-slate-400"}`}
                style={{ width: `${c.progress_pct}%` }}
              />
            </div>
            {(c.blockers > 0 || c.warnings > 0) && (
              <p className="mt-2 text-[11px] text-ink-mute">
                {c.blockers > 0 && <span className="text-red-600 font-semibold">{c.blockers} pemblokir</span>}
                {c.blockers > 0 && c.warnings > 0 && <span> · </span>}
                {c.warnings > 0 && <span className="text-amber-600 font-semibold">{c.warnings} peringatan</span>}
              </p>
            )}
          </div>
        ))}
        {!list.length && (
          <p className="col-span-full rounded-lg bg-slate-50 px-3 py-2 text-xs text-ink-mute">
            SOP belum dikonfigurasi. Hubungi Owner.
          </p>
        )}
      </div>
    </SectionCard>
  );
}

function WasteCard({ waste, onOpen }) {
  return (
    <SectionCard
      title="Waste Hari Ini"
      subtitle={`Ringkasan waste terekam hari ${waste?.date}`}
      icon={Trash2}
      testId="kops-waste"
      action={<Button size="sm" variant="ghost" onClick={onOpen} data-testid="kops-waste-open">Buka Waste <ArrowRight size={14} /></Button>}
    >
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Laporan" value={waste?.count ?? 0} icon={Trash2} testId="kops-waste-count" />
        <StatCard label="Total Qty" value={(waste?.total_qty ?? 0).toLocaleString("id-ID")} testId="kops-waste-qty" />
        <StatCard
          label="Nilai (Rp)"
          value={new Intl.NumberFormat("id-ID").format(Math.round(waste?.total_value ?? 0))}
          tone={waste?.total_value > 0 ? "warn" : "mute"}
          testId="kops-waste-value"
        />
      </div>
    </SectionCard>
  );
}

function AlertsCard({ alerts, onOpen }) {
  const recent = alerts?.recent || [];
  return (
    <SectionCard
      title="Alerts Hari Ini"
      subtitle={`${alerts?.total ?? 0} notifikasi · ${alerts?.unread ?? 0} belum dibaca`}
      icon={Bell}
      testId="kops-alerts"
      action={<Button size="sm" variant="ghost" onClick={onOpen} data-testid="kops-alerts-open">Semua Notifikasi <ArrowRight size={14} /></Button>}
    >
      <div className="mb-3 grid grid-cols-3 gap-2">
        <StatCard label="Kritis" value={alerts?.critical ?? 0} tone="danger" icon={ShieldAlert} testId="kops-alerts-critical" />
        <StatCard label="Peringatan" value={alerts?.warning ?? 0} tone="warn" icon={AlertTriangle} testId="kops-alerts-warning" />
        <StatCard label="Info" value={alerts?.info ?? 0} tone="info" icon={Info} testId="kops-alerts-info" />
      </div>
      {recent.length === 0 && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-ink-mute">Tidak ada alert hari ini.</p>
      )}
      {recent.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line" data-testid="kops-alerts-list">
          {recent.slice(0, 5).map((n) => (
            <li key={n.id} className="p-2.5">
              <div className="flex items-start gap-2">
                <Badge tone={n.severity === "danger" ? "danger" : n.severity === "warning" ? "warn" : "info"} className="shrink-0 mt-0.5">
                  {n.severity === "danger" ? "🔴" : n.severity === "warning" ? "🟡" : "🔵"}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{n.title}</p>
                  <p className="line-clamp-2 text-xs text-ink-mute">{n.body}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function QuickActionsCard({ actions }) {
  return (
    <SectionCard title="Aksi Cepat" subtitle="Navigasi langsung ke halaman operasional" icon={Sparkles} testId="kops-quick-actions">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {actions.map((a) => (
          <button
            key={a.testId}
            onClick={a.onClick}
            className="flex flex-col items-center gap-1.5 rounded-lg border border-line bg-white p-3 text-center transition hover:border-orange-300 hover:bg-orange-50"
            data-testid={a.testId}
          >
            <a.icon size={20} className="text-orange-600" />
            <span className="text-xs font-semibold text-ink">{a.label}</span>
          </button>
        ))}
      </div>
    </SectionCard>
  );
}

function BranchesOverview({ rows, onOpenBranch }) {
  return (
    <SectionCard
      title="Ringkasan per Cabang"
      subtitle="Klik cabang untuk membuka Dashboard Dapur cabang tersebut"
      icon={Store}
      testId="kops-branches-overview"
    >
      <ul className="divide-y divide-line rounded-lg border border-line" data-testid="kops-branches-list">
        {(rows || []).map((r) => {
          const band = r.readiness_band || "not_ready";
          const score = r.readiness_score ?? 0;
          return (
            <li key={r.branch_id}>
              <button
                onClick={() => onOpenBranch(r.branch_id)}
                className="flex w-full items-center gap-3 p-3 text-left transition hover:bg-orange-50"
                data-testid={`kops-branch-row-${r.branch_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-bold text-ink">{r.branch_name}</p>
                  <p className="text-xs text-ink-mute">
                    {r.critical} kritis · {r.not_started + r.partial} belum siap · Total {r.total_active} item
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-black tabular-nums ${BAND_TEXT_CLS[band]}`}>{score}%</p>
                  <Badge tone={BAND_TONE[band]} className="mt-0.5">{BAND_EMOJI[band]} {BAND_LABEL[band]}</Badge>
                </div>
                <ArrowRight size={16} className="text-ink-mute" />
              </button>
            </li>
          );
        })}
        {!rows?.length && (
          <li className="p-4 text-center text-sm text-ink-mute">Belum ada data readiness cabang.</li>
        )}
      </ul>
    </SectionCard>
  );
}

export default function KitchenOpsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [warehouseParam, setWarehouseParam] = useState(params.get("warehouse") || null);
  const [warehouses, setWarehouses] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [data, setData] = useState(null);
  const [branchRows, setBranchRows] = useState(null);
  const [loading, setLoading] = useState(true);

  const isOwnerScope = user?.role === "OWNER" || user?.role === "SUPER_ADMIN";

  const loadWarehouses = useCallback(async () => {
    try {
      const { data } = await api.get("/prepare/warehouses");
      setWarehouses(data.warehouses || []);
      setCanSwitch(!!data.can_switch);
      setWarehouseParam((cur) => cur || data.active?.id || "central");
    } catch { /* silent — dashboard endpoint will fallback */ }
  }, []);

  const loadDashboard = useCallback(async (wh) => {
    setLoading(true);
    try {
      const q = wh ? `?warehouse=${encodeURIComponent(wh)}` : "";
      const { data } = await api.get(`/kitchen-ops/dashboard${q}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat dashboard");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadBranchesOverview = useCallback(async () => {
    if (!isOwnerScope) return;
    try {
      const { data } = await api.get("/kitchen-ops/branches-overview");
      setBranchRows(data.branches || []);
    } catch { /* non-fatal */ }
  }, [isOwnerScope]);

  useEffect(() => { loadWarehouses(); }, [loadWarehouses]);
  useEffect(() => { if (warehouseParam) loadDashboard(warehouseParam); }, [warehouseParam, loadDashboard]);
  useEffect(() => { loadBranchesOverview(); }, [loadBranchesOverview]);

  const changeWarehouse = (wh) => {
    setWarehouseParam(wh);
    const p = new URLSearchParams(params);
    if (wh) p.set("warehouse", wh); else p.delete("warehouse");
    setParams(p, { replace: true });
  };

  const goPrepare = useCallback((preset) => {
    // Reuse Meja Prepare with the same router-state preset contract Phase 1 introduced.
    if (preset?.source_product_id) {
      navigate("/meja-prepare", {
        state: {
          preset: {
            source_product_id: preset.source_product_id,
            suggested_qty: preset.need_prepare,
            output_ids: [preset.output_product_id],
          },
          warehouse_id: warehouseParam,
        },
      });
    } else {
      navigate("/meja-prepare");
    }
  }, [navigate, warehouseParam]);

  const canConfigureSOP = user?.role === "OWNER" || user?.role === "SUPER_ADMIN";
  const canOpenCentralProduction = user?.role === "OWNER" || user?.role === "SUPER_ADMIN"
    || (user?.role === "KITCHEN" && user?.kitchen_type === "central");
  // Head Chef executes SOP inside Meja Prepare (accordions, per Phase 2B contract).
  // Owner/SA manage SOP templates on the config page.
  const sopRoute = canConfigureSOP ? "/kitchen-sop" : "/meja-prepare";

  const quickActions = useMemo(() => {
    const base = [
      { label: "Meja Prepare", icon: ChefHat, testId: "kops-qa-prepare", onClick: () => navigate("/meja-prepare") },
      { label: "SOP Dapur", icon: ClipboardCheck, testId: "kops-qa-sop", onClick: () => navigate(sopRoute) },
      { label: "Bahan Siap Masak", icon: Package, testId: "kops-qa-inventory", onClick: () => navigate("/my-inventory") },
      { label: "Waste", icon: Trash2, testId: "kops-qa-waste", onClick: () => navigate("/waste") },
    ];
    if (canOpenCentralProduction) {
      base.push({ label: "Produksi Central", icon: Factory, testId: "kops-qa-production", onClick: () => navigate("/central-production") });
    }
    return base;
  }, [navigate, sopRoute, canOpenCentralProduction]);

  if (loading && !data) return <PageLoader />;

  return (
    <div className="space-y-4 pb-8" data-testid="kitchen-ops-dashboard">
      <PageHeader
        title="Dashboard Dapur"
        subtitle={data?.warehouse?.name ? `Monitoring & KPI · ${data.warehouse.name} · ${data.date}` : "Monitoring & KPI"}
        icon={LayoutDashboard}
      >
        {canSwitch && warehouses.length > 1 && (
          <select
            value={warehouseParam || ""}
            onChange={(e) => changeWarehouse(e.target.value)}
            className="rounded-lg border border-line bg-white px-2 py-1.5 text-sm"
            data-testid="kops-warehouse-select"
          >
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => loadDashboard(warehouseParam)}
          data-testid="kops-refresh"
        >
          <RefreshCw size={14} /> Muat Ulang
        </Button>
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-3">
        <ReadinessCard readiness={data?.readiness} onOpen={() => navigate("/meja-prepare")} />
        <div className="lg:col-span-2">
          <TodayPrepCard summary={data?.prep_summary} onOpen={() => navigate("/meja-prepare")} />
        </div>
      </div>

      <CriticalRtcCard items={data?.critical_rtc} onPrepare={goPrepare} />

      <div className="grid gap-4 lg:grid-cols-2">
        <SopStatusCard
          categories={data?.sop_categories}
          blockersTotal={data?.sop_blockers_total || 0}
          warningsTotal={data?.sop_warnings_total || 0}
          onOpen={() => navigate(sopRoute)}
          openLabel={canConfigureSOP ? "Kelola SOP" : "Kerjakan SOP"}
        />
        <WasteCard waste={data?.waste_today} onOpen={() => navigate("/waste")} />
      </div>

      <AlertsCard alerts={data?.alerts} onOpen={() => navigate("/notifications")} />

      <QuickActionsCard actions={quickActions} />

      {isOwnerScope && (
        <BranchesOverview rows={branchRows} onOpenBranch={(bid) => changeWarehouse(bid)} />
      )}
    </div>
  );
}
